import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import { useTranslation } from 'react-i18next'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import LayoutContainer from 'Components/LayoutComponent'
import { DialogModal } from 'Components/UIComponents'
import { ROUTE_STRINGS } from 'Utils/Constants'
import apiAdapterCoffeeWeb from 'Services/apiAdapterCoffeeWeb'
import { commonMethods } from 'Utils/commonMethods'
import Loader from 'Components/Loader'
import YearMonthNavigation from 'Components/UIComponents/YearMonthNavigation'
import AddTradeInvoice from 'Screens/TradeInvoice/AddTradeInvoice'
import EditTradeInvoice from 'Screens/TradeInvoice/EditTradeInvoice'
import PreviewTradeInvoice from './PreviewTradeInvoice'

const ListTradeInvoice = ({ setLoading, isLoading }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const history = useHistory()

  const { utcToLocalShortDate } = commonMethods

  const [showDialog, setShowDialog] = useState({ showModal: false, modalType: '', message: '', onHide: () => {}, onRetry: () => {}, onConfirmation: () => {}, onSuccess: () => {} })

  const [availableYears, setAvailableYears] = useState([])
  const [monthlyTradeInvoices, setMonthlyTradeInvoices] = useState([])
  const [monthsForSelectedYear, setMonthsForSelectedYear] = useState([])
  const [tradeInvoiceYears, setTradeInvoiceYears] = useState([])
  const [selectedInvoiceDetails, setSelectedInvoiceDetails] = useState({})
  const [selectedYearListIndex, setSelectedYearListIndex] = useState(null)
  const [selectedInvoiceMonth, setSelectedInvoiceMonth] = useState(null)
  const [selectedInvoiceYear, setSelectedInvoiceYear] = useState(null)
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [activeModule, setActiveModule] = useState('')

  useEffect(() => {
    const query = new URLSearchParams(location.search)
    const page = query.get('page')

    if (page === 'list') {
      setActiveModule('list')
    } else if (page === 'add') {
      setActiveModule('add')
    } else if (page === 'edit') {
      setActiveModule('edit')
    }
  }, [window.location.search])

  const getPageTitle = () => {
    /* eslint-disable no-else-return */
    if (activeModule === 'list') {
      return t('LIST_TRADE_INVOICE')
    } else if (activeModule === 'add') {
      return t('ADD_TRADE_INVOICE')
    } else if (activeModule === 'edit') {
      return t('EDIT_TRADE_INVOICE')
    }
  }

  const getBreadCrumbs = () => {
    if (activeModule === 'list') {
      return 'listTradeInvoice'
    } else if (activeModule === 'add') {
      return 'addTradeInvoice'
    } else {
      return 'editTradeInvoice'
    }
  }

  useEffect(() => {
    fetchAllTradersInvoiceYears()
  }, [])

  useEffect(() => {
    const query = new URLSearchParams(location.search)
    const page = query.get('page')
    const year = query.get('year')
    const month = query.get('month')

    setActiveModule(page || 'list')

    if (year) {
      const yearIndex = availableYears.findIndex((y) => String(y.year) === String(year))

      if (yearIndex !== -1) {
        setSelectedYearListIndex(yearIndex)
        setMonthsForSelectedYear(availableYears[yearIndex])
        setSelectedInvoiceYear(Number(year))
      }
    } else {
      setSelectedYearListIndex(null)
      setMonthsForSelectedYear([])
      setSelectedInvoiceYear(null)
      setSearchTerm('')
    }

    if (year && month) {
      getTradersInvoiceMonthList(year, month)
      setSelectedInvoiceMonth({ name: month })
    } else {
      setSelectedInvoiceMonth(null)
      setMonthlyTradeInvoices([])
      setSearchTerm('')
    }
  }, [location.search, availableYears])

  const fetchAllTradersInvoiceYears = async () => {
    setLoading(true)
    setShowDialog({ ...showDialog, showModal: false })

    try {
      const response = await apiAdapterCoffeeWeb.getAllTradersInvoiceList()

      const yearResponse = response?.data?.returnLst

      const yearData = yearResponse?.map((item) => ({
        year: item.year
      }))

      if (yearResponse) {
        setAvailableYears(yearResponse)
        setTradeInvoiceYears(yearData)
      }
    } catch {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('FAILED_TO_GET_TRADE_INVOICE_LIST'),
        onRetry: () => fetchAllTradersInvoiceYears(),
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const getTradersInvoiceMonthList = async (year, month) => {
    setLoading(true)
    setShowDialog({ ...showDialog, showModal: false })

    const retryFunction = () => getTradersInvoiceMonthList(year, month)

    try {
      const response = await apiAdapterCoffeeWeb.getAllTradersInvoiceListByMonth({
        year,
        month
      })

      const monthResponse = response?.data?.returnLst

      const modifiedData = monthResponse?.map((item) => {
        const paymentDate = commonMethods.utcToLocalTime(item.paymentDate)

        return {
          ...item,
          paymentDate
        }
      })

      setMonthlyTradeInvoices(modifiedData)
    } catch (error) {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: t('FAILED_TO_GET_TRADE_INVOICE_LIST'),
        onRetry: () => retryFunction(),
        onHide: handleDismiss
      })
    } finally {
      setLoading(false)
    }
  }

  const deleteConfirmation = async (rowData) => {
    const { invoiceId } = rowData

    handleDismiss()
    setLoading(true)
    try {
      const res = await apiAdapterCoffeeWeb.deleteTradeInvoiceById(invoiceId)

      if (res?.data) {
        const updatedTradeInvoices = monthlyTradeInvoices.filter((ele) => ele?.invoiceId !== invoiceId)

        setMonthlyTradeInvoices(updatedTradeInvoices)

        setShowDialog({
          ...showDialog,
          showModal: true,
          modalType: 'success',
          message: t('TRADE_INVOICE_DELETED_SUCCESSFULLY'),
          onHide: handleSuccess,
          onSuccess: handleSuccess
        })
      }
    } catch (error) {
      setShowDialog({
        ...showDialog,
        showModal: true,
        modalType: 'error',
        message: error.response.data.message,
        onHide: handleDismiss,
        onRetry: deleteConfirmation
      })
    } finally {
      setLoading(false)
    }
  }

  const handleClickDeleteButton = (rowData) => {
    setShowDialog({
      ...showDialog,
      showModal: true,
      modalType: 'info',
      message: t('ARE_YOU_SURE_WANT_TO_DELETE_THIS_TRADE_INVOICE'),
      onConfirmation: () => deleteConfirmation(rowData),
      onHide: handleDismiss
    })
  }

  const renderCompanyNameColumn = (item) => <div>{item?.buyerCompanyDetails?.companyName}</div>

  const renderGrandTotalColumn = (rowData) => <div>₹ {rowData?.totalAmount?.toLocaleString('en-US')}</div>

  const renderContractNumberColumn = (rowData) => <div>{rowData.contractDetails.contractNumber}</div>

  const renderDateColumn = (item) => {
    const formattedDate = utcToLocalShortDate(item?.invoiceDate)

    return <div>{formattedDate}</div>
  }

  const handleClickPreviewButton = (rowData) => {
    setShowPreviewModal(true)
    setSelectedInvoiceDetails(monthlyTradeInvoices?.find((ele) => ele.invoiceId === rowData.invoiceId))
  }

  const handleClickAddButton = () => {
    history.push({
      pathname: ROUTE_STRINGS.listTradeInvoice,
      search: 'page=add'
    })
  }

  const handleClickEditButton = (rowData) => {
    const searchParams = new URLSearchParams(location.search)
    const year = searchParams.get('year')
    const month = searchParams.get('month')

    const updatedSearchParams = new URLSearchParams({
      page: 'edit',
      'invoice-id': rowData.invoiceId
    })

    if (year) updatedSearchParams.set('year', year)
    if (month) updatedSearchParams.set('month', month)

    history.push({
      pathname: ROUTE_STRINGS.listTradeInvoice,
      search: updatedSearchParams.toString()
    })
  }

  const canEditInvoice = (createdAtUTC) => {
    if (!createdAtUTC) return false

    const invoiceCreatedDate = new Date(createdAtUTC)

    const now = new Date()

    const diffInMs = now - invoiceCreatedDate
    const diffInHours = diffInMs / (1000 * 60 * 60)

    return diffInHours <= 48
  }

  const renderActionButtons = (rowData) => {
    const isEditable = canEditInvoice(rowData?.createdAt)

    return (
      <div className="action-buttons-wrapper">
        <Button type="button" icon="pi pi-eye" className="preview-button" tooltip={t('PREVIEW')} tooltipOptions={{ position: 'top' }} onClick={() => handleClickPreviewButton(rowData)} />
        <Button type="button" icon="pi pi-pencil" className="edit-button" tooltip={t('EDIT')} tooltipOptions={{ position: 'top' }} onClick={() => isEditable && handleClickEditButton(rowData)} disabled={!isEditable} />
        <Button type="button" icon="pi pi-trash" className="delete-button" tooltip={t('DELETE')} tooltipOptions={{ position: 'top' }} onClick={() => handleClickDeleteButton(rowData)} />
      </div>
    )
  }

  const columns = [
    {
      dataField: 'invoiceDate',
      text: t('INVOICE_DATE'),
      body: (e) => renderDateColumn(e),
      className: 'invoice-date-column'
    },
    {
      dataField: 'invoiceNumber',
      text: t('INVOICE_NUMBER'),
      className: 'invoice-number-column'
    },
    {
      dataField: 'companyName',
      text: t('COMPANY_NAME'),
      body: (e) => renderCompanyNameColumn(e),
      className: 'company-name-column'
    },
    {
      dataField: 'contractNumber',
      text: t('CONTRACT_NUMBER'),
      body: (e) => renderContractNumberColumn(e),
      className: 'contract-number-column'
    },
    {
      dataField: 'grandTotal',
      text: t('GRAND_TOTAL'),
      body: (e) => renderGrandTotalColumn(e),
      className: 'grand-total-column'
    },
    {
      dataField: 'quantity',
      text: t('ACTIONS'),
      body: (e) => renderActionButtons(e),
      className: 'actions-column'
    }
  ]

  const handleSuccess = () => setShowDialog({ ...showDialog, showModal: false })
  const handleDismiss = () => setShowDialog({ ...showDialog, showModal: false })

  const handleYearClick = (item, index) => {
    const selectedYear = item.year

    setMonthsForSelectedYear(availableYears[index])
    setSelectedYearListIndex(index)
    setSelectedInvoiceYear(item.year)

    history.push({
      search: `?page=list&year=${selectedYear}`
    })
  }

  const handleMonthClick = (month) => {
    setSelectedInvoiceMonth(month)

    history.push({
      search: `?page=list&year=${monthsForSelectedYear?.year}&month=${month?.name}`
    })
  }

  const handleYearChange = (value) => {
    const yearIndex = availableYears?.findIndex((item) => item.year === value)

    if (yearIndex !== -1 && yearIndex !== undefined) {
      const yearData = availableYears[yearIndex]

      setSelectedYearListIndex(yearIndex)
      setMonthsForSelectedYear(yearData)
      setSelectedInvoiceYear(value)

      history.push({
        search: `?page=list&year=${value}`
      })
    }
  }

  const handleMonthBack = () => {
    const updatedParams = new URLSearchParams(location.search)

    updatedParams.delete('month')

    history.push({
      pathname: ROUTE_STRINGS.listTradeInvoice,
      search: updatedParams.toString()
    })

    setSearchTerm('')
  }

  const handleMonthAndYearBack = () => {
    const updatedParams = new URLSearchParams(location.search)

    updatedParams.delete('year')
    updatedParams.delete('month')

    history.push({
      pathname: ROUTE_STRINGS.listTradeInvoice,
      search: updatedParams.toString()
    })

    setSearchTerm('')
  }

  const handleSearchChange = (value) => {
    setSearchTerm(value)
  }

  const handlePrevious = () => {
    if (selectedYearListIndex > 0) {
      const newIndex = selectedYearListIndex - 1

      setSelectedYearListIndex(newIndex)
      setMonthsForSelectedYear(availableYears[newIndex])
      setSelectedInvoiceYear(availableYears[newIndex].year)

      const updatedParams = new URLSearchParams(location.search)

      updatedParams.set('year', availableYears[newIndex].year)
      updatedParams.delete('month')

      history.push({
        pathname: ROUTE_STRINGS.listTradeInvoice,
        search: updatedParams.toString()
      })
    }
  }

  const handleNext = () => {
    if (selectedYearListIndex < availableYears.length - 1) {
      const newIndex = selectedYearListIndex + 1

      setSelectedYearListIndex(newIndex)
      setMonthsForSelectedYear(availableYears[newIndex])
      setSelectedInvoiceYear(availableYears[newIndex].year)

      const updatedParams = new URLSearchParams(location.search)

      updatedParams.set('year', availableYears[newIndex].year)
      updatedParams.delete('month')

      history.push({
        pathname: ROUTE_STRINGS.listTradeInvoice,
        search: updatedParams.toString()
      })
    }
  }

  const filterInvoicesByMonth = monthlyTradeInvoices?.filter((item) => item.invoiceNumber?.toString().toLowerCase().includes(searchTerm.toLowerCase()) || item.buyerCompanyDetails?.buyerName?.toLowerCase().includes(searchTerm.toLowerCase()) || item.buyerCompanyDetails?.phoneNumber?.toString().includes(searchTerm) || utcToLocalShortDate(item.invoiceDate)?.toLowerCase().includes(searchTerm.toLowerCase()))

  return (
    <LayoutContainer title={getPageTitle()} breadCrumbsTitle={getBreadCrumbs()} showAddButton={activeModule === 'list' && !!selectedInvoiceYear && !!selectedInvoiceMonth} handleAddButtonClick={handleClickAddButton}>
      <div className="trade-invoice-component-wrapper">
        {activeModule === 'list' && <YearMonthNavigation availableReportYears={availableYears} availableReportMonths={monthsForSelectedYear} onYearFolderClick={handleYearClick} onMonthFolderClick={handleMonthClick} activeYearIndex={selectedYearListIndex} onYearDecrement={handlePrevious} onYearIncrement={handleNext} navigateToYearPage={handleMonthAndYearBack} availableYearsList={tradeInvoiceYears} yearDropdownLabelKey="year" yearDropdownValueKey="year" yearDropdownValue={selectedInvoiceYear} handleYearDropdownChange={handleYearChange} reportTableColumns={columns} reportTableData={filterInvoicesByMonth} tableClassName="trader-invoice-data-table" loading={isLoading} activeYear={monthsForSelectedYear?.year} activeMonth={selectedInvoiceMonth?.name} navigateToMonthPage={handleMonthBack} showMultiSelectFilter={false} handleSearchInputChange={handleSearchChange} searchInputTextValue={searchTerm} />}
        {activeModule === 'add' && <AddTradeInvoice tradeInvoiceList={monthlyTradeInvoices} setTradeInvoiceList={setMonthlyTradeInvoices} />}
        {activeModule === 'edit' && <EditTradeInvoice tradeInvoiceList={monthlyTradeInvoices} setTradeInvoiceList={setMonthlyTradeInvoices} />}

        <Dialog className="trade-invoice-preview-modal-popup" header="Preview Trade Invoice" visible={showPreviewModal} style={{ height: '100vh' }} onHide={() => setShowPreviewModal(false)}>
          <PreviewTradeInvoice invoiceDetails={selectedInvoiceDetails} />
        </Dialog>

        <DialogModal {...showDialog} onDismiss={handleDismiss} />
      </div>
    </LayoutContainer>
  )
}

export default Loader(ListTradeInvoice)
